import {Button, type ButtonBaseProps, IconButton} from '@primer/react'
import {CodeIcon} from '@primer/octicons-react'
import type {Model} from '@github-ui/marketplace-common'
import {sendEvent, type SendEventContext} from '@github-ui/hydro-analytics'
import {useUser} from '@github-ui/use-user'
import {useMediaQuery} from '@github-ui/use-media-query'
import {testIdProps} from '@github-ui/test-id-props'
import {GettingStartedButtonClicked} from '../utils/playground-types'
import {NARROW_VIEWPORT_MEDIA_QUERY} from '../utils/breakpoints'

interface UseThisModelButtonProps extends Pick<
  ButtonBaseProps,
  'onClick' | 'variant' | 'block' | 'tabIndex' | 'className'
> {
  model: Model
  hideLabelOnSmallScreens?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export function UseThisModelButton({
  ref: forwardedRef,
  hideLabelOnSmallScreens = true,
  model,
  ...buttonProps
}: UseThisModelButtonProps) {
  const {currentUser} = useUser()
  const onClick = buttonProps.onClick
  const label = 'Use this model'
  // useResponsiveValue is deprecated (github/primer#6847). CSS render-both isn't safe here since
  // Button and IconButton are different element types sharing one forwardedRef used for Dialog
  // focus-restoration; rendering both would leave the ref on whichever mounts last, not the visible one.
  const isMobile = useMediaQuery(NARROW_VIEWPORT_MEDIA_QUERY)

  const commonButtonProps: Omit<ButtonBaseProps, 'aria-label' | 'aria-labelledby'> = {
    ...buttonProps,
    onClick: evt => {
      if (onClick) onClick(evt)
      const payload: SendEventContext = {
        registry: model.registry,
        model: model.name,
        publisher: model.publisher,
        label,
        analyticsTrackingId: currentUser?.analyticsTrackingId,
      }
      sendEvent(GettingStartedButtonClicked, payload)
    },
    ...testIdProps('get-api-key-button'),
  }

  if (!isMobile || !hideLabelOnSmallScreens) {
    return (
      <Button leadingVisual={CodeIcon} {...commonButtonProps} ref={forwardedRef}>
        {label}
      </Button>
    )
  }

  return <IconButton {...commonButtonProps} aria-label={label} icon={CodeIcon} ref={forwardedRef} />
}
